<main class="container-fluid tw-flex tw-flex-col tw-gap-4">
  <h2 class="tw-mb-0 tw-text-2xl tw-font-bold tw-tracking-wide">
    {{ 'dashboard.dashboardTitle' | translate }}
    <span class="tw-text-primary">{{ fullName }}</span
    >!
  </h2>
  <p class="tw-mt-1 tw-text-sm tw-text-black">{{ 'dashboard.dashboardDesc' | translate }}</p>

  <!-- FRC-987: info tooltip beside the donut heading. Shared by the desktop heading and
     the in-donut mobile heading; the copy tracks the active view via titleTooltipKey, so
     one trigger covers all three states (Total Assets / Shares / Cash).
     container="body" keeps the popup out of the donut's clipped/transformed stacking context. -->
  <ng-template #donutInfo let-sizeClass="sizeClass">
    <button
      type="button"
      class="pitchin-icon-info-circle tw-shrink-0 tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0"
      [ngClass]="sizeClass"
      placement="top"
      container="body"
      [attr.aria-label]="titleTooltipKey | translate"
      [ngbTooltip]="titleTooltipKey | translate"
    ></button>
  </ng-template>

  <section class="tw-flex tw-flex-col tw-items-stretch tw-gap-4 xl:tw-flex-row">
    <!-- ============ TOTAL ASSETS ============ -->
    <article
      class="tw-flex tw-flex-1 tw-flex-col tw-items-center tw-gap-5 xl:tw-gap-10 xl:tw-rounded-2xl xl:tw-border xl:tw-border-divider xl:tw-bg-white xl:tw-p-6"
    >
      <!-- Heading + Back: desktop only. Mobile shows the donut in its own card with no heading (Figma 222:419). -->
      <div class="tw-hidden tw-flex-col tw-items-center tw-gap-2 xl:tw-flex">
        <div class="tw-flex tw-items-center tw-gap-2">
          <h3
            class="tw-m-0 tw-text-2xl tw-font-bold tw-uppercase tw-tracking-[0.96px] tw-text-black"
          >
            {{ title }}
          </h3>
          <ng-container
            *ngTemplateOutlet="donutInfo; context: { sizeClass: 'tw-h-4 tw-w-4' }"
          ></ng-container>
        </div>
        <!-- Back: returns from a drilled-in slice to the Total Assets overview -->
        <button
          *ngIf="selectedSegment"
          type="button"
          (click)="clearSelection()"
          class="tw-hidden tw-cursor-pointer tw-items-center tw-gap-1 tw-border-0 tw-bg-transparent tw-p-0 tw-text-sm tw-font-semibold tw-text-secondary xl:tw-flex"
          [attr.aria-label]="'dashboard.a11y.backToTotalAssets' | translate"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
          {{ 'common.back' | translate }}
        </button>
      </div>

      <!-- Donut card: own bordered/shadowed card on mobile (Figma 222:419); plain wrapper on desktop (the article is the card). -->
      <div
        class="tw-flex tw-w-full tw-flex-col tw-items-center tw-gap-[15px] tw-rounded-2xl tw-border tw-border-divider tw-bg-white tw-px-4 tw-pb-4 tw-pt-6 tw-shadow-[0_2px_12px_0_rgba(0,0,0,0.08)] xl:tw-gap-0 xl:tw-rounded-none xl:tw-border-0 xl:tw-bg-transparent xl:tw-p-0 xl:tw-shadow-none"
      >
        <!-- Loading: donut hidden until both cash + shares resolve -->
        <div
          *ngIf="isLoading"
          class="tw-flex tw-h-[220px] tw-w-[220px] tw-items-center tw-justify-center"
          role="status"
          [attr.aria-label]="'dashboard.a11y.loadingTotalAssets' | translate"
        >
          <svg
            class="tw-h-10 tw-w-10 tw-animate-spin tw-text-primary"
            viewBox="0 0 24 24"
            fill="none"
          >
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
              class="tw-opacity-25"
            />
            <path
              d="M4 12a8 8 0 0 1 8-8"
              stroke="currentColor"
              stroke-width="4"
              stroke-linecap="round"
            />
          </svg>
        </div>

        <!-- Donut (data-driven: one arc per segment; click a slice to drill in) -->
        <div class="donut" *ngIf="!isLoading" [class.donut--expanded]="isBreakdownExpanded">
          <svg viewBox="0 0 220 220" class="donut__svg">
            <!-- Empty track: only when there are no holdings, so the ring stays visible.
               Guarded so it never sits behind real arcs (would tint the 4px white dividers grey). -->
            <circle
              *ngIf="totalAssets === 0 || hasWithheldShares"
              class="donut__track"
              cx="110"
              cy="110"
              [attr.r]="radius"
              fill="none"
              [attr.stroke]="mutedColor"
              [attr.stroke-width]="strokeWidth"
            />
            <!-- FRC-1029: no clickable arcs while withheld — there is nothing behind the
               ring the investor is entitled to drill into yet. -->
            <ng-container *ngIf="!hasWithheldShares">
              <circle
                *ngFor="let arc of donutArcs; trackBy: trackArc"
                class="donut__seg donut__seg--clickable"
                cx="110"
                cy="110"
                [attr.r]="radius"
                [attr.stroke]="arc.color"
                [attr.stroke-width]="strokeWidth"
                [attr.stroke-dasharray]="arc.dasharray"
                [attr.stroke-dashoffset]="arc.dashoffset"
                tabindex="0"
                role="button"
                [attr.aria-label]="segmentAriaLabel(arc.index)"
                (click)="selectSegment(arc.index)"
                (keydown.enter)="selectSegment(arc.index)"
                (keydown.space)="$event.preventDefault(); selectSegment(arc.index)"
              />
            </ng-container>
          </svg>

          <div class="donut__center">
            <!-- Mobile heading: reflects the active view (Total Assets / Cash / Shares).
               Desktop shows this heading above the donut instead. -->
            <div class="tw-flex tw-items-center tw-gap-1 xl:tw-hidden">
              <p
                class="tw-m-0 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-body"
              >
                {{ title }}
              </p>
              <ng-container
                *ngTemplateOutlet="donutInfo; context: { sizeClass: 'tw-h-3 tw-w-3' }"
              ></ng-container>
            </div>
            <!-- FRC-1029 state B: the figure is withheld, not zero. Lock + mask stand in
               for the whole total — naming any amount would disclose a shareholding to an
               identity proven only by an email match. -->
            <span
              *ngIf="hasWithheldShares"
              class="todo-card__icon tw-mb-2 tw-bg-warning-light tw-text-warning-dark"
              aria-hidden="true"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle cx="12" cy="16" r="1" />
                <rect x="3" y="10" width="18" height="12" rx="2" />
                <path d="M7 10V7a5 5 0 0 1 10 0v3" />
              </svg>
            </span>
            <p
              class="donut__amount tw-m-0 tw-font-semibold tw-text-body-dark xl:tw-font-extrabold xl:tw-text-black"
              [class.donut__amount--expanded]="isBreakdownExpanded"
            >
              <!-- "RM ***" announces as bare "RM". aria-label is not reliably exposed on a
                 <p> (no nameable role), so the mask is hidden from assistive tech and the
                 meaning is carried by visually-hidden text instead. -->
              <ng-container *ngIf="hasWithheldShares">
                <span aria-hidden="true">RM ***</span>
                <span class="sr-only">{{ 'dashboard.withheldShares' | translate }}</span>
              </ng-container>
              <ng-container *ngIf="!hasWithheldShares"
                >RM {{ displayValue | number: '1.0-0' }}</ng-container
              >
            </p>

            <!-- Desktop only: in-donut breakdown + View Details.
               Mobile uses the tab bar + static breakdown row below the donut. -->
            <div class="tw-hidden xl:tw-flex xl:tw-flex-col xl:tw-items-center xl:tw-gap-1.5">
              <!-- Cash breakdown -->
              <ng-container *ngIf="selectedSegment?.key === 'cash'">
                <p
                  class="donut__detail tw-m-0 tw-font-semibold"
                  [class.donut__detail--expanded]="isBreakdownExpanded"
                >
                  <span class="tw-text-body">{{ 'dashboard.available' | translate }}: </span>
                  <!-- FRC-995: withdrawable balance (balance - hold), NOT the gross
                     account balance - that already includes the On Hold row below. -->
                  <span class="tw-text-secondary"
                    >+RM {{ cashDetail.available | number: '1.0-0' }}</span
                  >
                </p>
                <p
                  class="donut__detail tw-m-0 tw-font-semibold"
                  [class.donut__detail--expanded]="isBreakdownExpanded"
                >
                  <span class="tw-text-body">{{ 'dashboard.onHold' | translate }}: </span>
                  <span class="tw-text-black">RM {{ onHoldExcludingEcf | number: '1.0-0' }}</span>
                </p>
                <!-- FRC-991: ECF paid but not yet allotted. Hidden at 0 so investors with
                   no open pledges don't get a permanent "RM 0" row. -->
                <p
                  *ngIf="cashDetail.unallottedEcf > 0"
                  class="donut__detail tw-m-0 tw-font-semibold"
                  [class.donut__detail--expanded]="isBreakdownExpanded"
                >
                  <span class="tw-text-body">{{ 'dashboard.ecfInvestment' | translate }}: </span>
                  <span class="tw-text-black"
                    >RM {{ cashDetail.unallottedEcf | number: '1.0-0' }}</span
                  >
                </p>
              </ng-container>

              <!-- Overview + Shares share the gain/loss layout.
                 GRC-2616: hidden entirely pre-eKYC — the G/L figures are redacted to 0,
                 and a row of "+RM 0" reads as real data rather than as withheld data. -->
              <ng-container
                *ngIf="(!selectedSegment || selectedSegment.key === 'shares') && !isSharesProvisional"
              >
                <button
                  type="button"
                  (click)="toggleBreakdown()"
                  [attr.aria-expanded]="isBreakdownExpanded"
                  class="donut__summary tw-m-0 tw-flex tw-cursor-pointer tw-items-center tw-gap-1.5 tw-border-0 tw-bg-transparent tw-p-0 tw-font-semibold"
                  [class.donut__summary--expanded]="isBreakdownExpanded"
                  [class.tw-text-secondary]="totalGainLoss >= 0"
                  [class.tw-text-primary]="totalGainLoss < 0"
                >
                  ({{ 'dashboard.totalGainLossShort' | translate }}: {{ totalGainLoss >= 0 ? '+' : '' }}RM
                  {{ totalGainLoss | number: '1.0-0' }})
                  <svg
                    width="12"
                    height="8"
                    viewBox="0 0 12 8"
                    fill="none"
                    class="tw-transition-transform"
                    [class.tw-rotate-180]="isBreakdownExpanded"
                  >
                    <path
                      d="M1 1.5 6 6.5 11 1.5"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
                <ng-container *ngIf="isBreakdownExpanded">
                  <p
                    class="donut__detail tw-m-0 tw-font-semibold"
                    [class.donut__detail--expanded]="isBreakdownExpanded"
                  >
                    <span class="tw-text-body">{{ 'portfolio.unrealised' | translate }}: </span>
                    <span
                      [class.tw-text-secondary]="unrealised >= 0"
                      [class.tw-text-primary]="unrealised < 0"
                      >{{ unrealised >= 0 ? '+' : '' }}RM {{ unrealised | number: '1.0-0' }}</span
                    >
                  </p>
                  <p
                    class="donut__detail tw-m-0 tw-font-semibold"
                    [class.donut__detail--expanded]="isBreakdownExpanded"
                  >
                    <span class="tw-text-body">{{ 'portfolio.realised' | translate }}: </span>
                    <span
                      [class.tw-text-secondary]="realised >= 0"
                      [class.tw-text-primary]="realised < 0"
                      >{{ realised >= 0 ? '+' : '' }}RM {{ realised | number: '1.0-0' }}</span
                    >
                  </p>
                </ng-container>
              </ng-container>

              <!-- {{ 'dashboard.viewDetails' | translate }}: inside the donut, below the breakdown.
               Routes to the drilled-in segment's detail. FRC-987: omitted in the overview
               (Total Assets) state — there is no single segment to drill into, so the link
               has nothing meaningful to point at. Mirrors the mobile link below. -->
              <a
                *ngIf="selectedSegment"
                [routerLink]="selectedSegment.detailsLink"
                class="donut__link tw-mt-1 tw-font-semibold tw-text-secondary tw-underline hover:tw-text-secondary"
                [class.donut__link--expanded]="isBreakdownExpanded"
              >
                {{ 'dashboard.viewDetails' | translate }}
              </a>
            </div>
          </div>
        </div>

        <!-- ===== Mobile only: segmented control + static G/L breakdown (Figma 222:419) =====
           FRC-1029: hidden entirely in the withheld state. Pre-eKYC there is no cash (the
           wallet cannot be activated before eKYC) and the shares side is redacted, so every
           tab would drill into a masked or empty view. -->
        <div
          *ngIf="!isLoading && !hasWithheldShares"
          class="tw-flex tw-w-full tw-flex-col tw-gap-4 xl:tw-hidden"
        >
          <!-- Tab bar: All / Shares / Cash -->
          <div
            role="tablist"
            [attr.aria-label]="'dashboard.a11y.totalAssetsBreakdown' | translate"
            class="tw-flex tw-h-12 tw-w-full tw-items-center tw-gap-1 tw-rounded-xl tw-bg-divider-light tw-p-1"
          >
            <button
              type="button"
              role="tab"
              [attr.aria-selected]="selectedIndex === null"
              (click)="clearSelection()"
              [ngClass]="
                selectedIndex === null
                  ? 'tw-bg-white tw-text-black tw-shadow-sm'
                  : 'tw-bg-transparent tw-text-body'
              "
              class="tw-flex tw-h-10 tw-flex-1 tw-cursor-pointer tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-[10px] tw-border-0 tw-text-[13px] tw-font-semibold"
            >
              All
            </button>
            <button
              type="button"
              role="tab"
              [attr.aria-selected]="selectedSegment?.key === 'shares'"
              (click)="selectSegmentByKey('shares')"
              [ngClass]="
                selectedSegment?.key === 'shares'
                  ? 'tw-bg-white tw-text-black tw-shadow-sm'
                  : 'tw-bg-transparent tw-text-body'
              "
              class="tw-flex tw-h-10 tw-flex-1 tw-cursor-pointer tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-[10px] tw-border-0 tw-text-[13px] tw-font-semibold"
            >
              <span
                class="tw-h-[7px] tw-w-[7px] tw-shrink-0 tw-rounded-full"
                [style.background-color]="segmentColor('shares')"
              ></span>
              Shares
            </button>
            <button
              type="button"
              role="tab"
              [attr.aria-selected]="selectedSegment?.key === 'cash'"
              (click)="selectSegmentByKey('cash')"
              [ngClass]="
                selectedSegment?.key === 'cash'
                  ? 'tw-bg-white tw-text-black tw-shadow-sm'
                  : 'tw-bg-transparent tw-text-body'
              "
              class="tw-flex tw-h-10 tw-flex-1 tw-cursor-pointer tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-[10px] tw-border-0 tw-text-[13px] tw-font-semibold"
            >
              <span
                class="tw-h-[7px] tw-w-[7px] tw-shrink-0 tw-rounded-full"
                [style.background-color]="segmentColor('cash')"
              ></span>
              Cash
            </button>
          </div>

          <!-- Breakdown row.
             GRC-2616: pre-eKYC only the Cash breakdown has anything to say, so the whole
             row drops out on the All/Shares tabs rather than rendering an empty flex row. -->
          <div
            *ngIf="selectedSegment?.key === 'cash' || !isSharesProvisional"
            class="tw-flex tw-w-full tw-items-center tw-gap-3"
          >
            <!-- Overview + Shares: gain / loss -->
            <ng-container *ngIf="!selectedSegment || selectedSegment.key === 'shares'">
              <div class="tw-flex tw-flex-1 tw-flex-col tw-items-center tw-gap-0.5">
                <span class="tw-text-xs tw-font-semibold tw-leading-4 tw-text-body">{{ 'dashboard.totalGainLossShort' | translate }}</span>
                <span
                  class="tw-text-sm tw-font-semibold tw-leading-5"
                  [class.tw-text-secondary]="totalGainLoss >= 0"
                  [class.tw-text-primary]="totalGainLoss < 0"
                  >{{ totalGainLoss >= 0 ? '+' : '' }}RM {{ totalGainLoss | number: '1.0-0' }}</span
                >
              </div>
              <div class="tw-h-8 tw-w-px tw-shrink-0 tw-bg-divider"></div>
              <div class="tw-flex tw-flex-1 tw-flex-col tw-items-center tw-gap-0.5">
                <span class="tw-text-xs tw-font-semibold tw-leading-4 tw-text-body"
                  >{{ 'portfolio.unrealised' | translate }}</span
                >
                <span
                  class="tw-text-sm tw-font-semibold tw-leading-5"
                  [class.tw-text-secondary]="unrealised >= 0"
                  [class.tw-text-primary]="unrealised < 0"
                  >{{ unrealised >= 0 ? '+' : '' }}RM {{ unrealised | number: '1.0-0' }}</span
                >
              </div>
              <div class="tw-h-8 tw-w-px tw-shrink-0 tw-bg-divider"></div>
              <div class="tw-flex tw-flex-1 tw-flex-col tw-items-center tw-gap-0.5">
                <span class="tw-text-xs tw-font-semibold tw-leading-4 tw-text-body">{{ 'portfolio.realised' | translate }}</span>
                <span
                  class="tw-text-sm tw-font-semibold tw-leading-5"
                  [class.tw-text-secondary]="realised >= 0"
                  [class.tw-text-primary]="realised < 0"
                  >{{ realised >= 0 ? '+' : '' }}RM {{ realised | number: '1.0-0' }}</span
                >
              </div>
            </ng-container>
            <!-- Cash: available / on hold / unallotted ECF (FRC-991) -->
            <ng-container *ngIf="selectedSegment?.key === 'cash'">
              <div class="tw-flex tw-flex-1 tw-flex-col tw-items-center tw-gap-0.5">
                <span class="tw-text-xs tw-font-semibold tw-leading-4 tw-text-body">{{ 'dashboard.available' | translate }}</span>
                <!-- FRC-995: withdrawable balance (balance - hold), NOT the gross
                   account balance - that already includes the On Hold column. -->
                <span class="tw-text-sm tw-font-semibold tw-leading-5 tw-text-secondary"
                  >+RM {{ cashDetail.available | number: '1.0-0' }}</span
                >
              </div>
              <div class="tw-h-8 tw-w-px tw-shrink-0 tw-bg-divider"></div>
              <div class="tw-flex tw-flex-1 tw-flex-col tw-items-center tw-gap-0.5">
                <span class="tw-text-xs tw-font-semibold tw-leading-4 tw-text-body">{{ 'dashboard.onHold' | translate }}</span>
                <span class="tw-text-sm tw-font-semibold tw-leading-5 tw-text-black"
                  >RM {{ onHoldExcludingEcf | number: '1.0-0' }}</span
                >
              </div>
              <!-- Hidden at 0 so the row stays two-up for investors with no open pledges. -->
              <ng-container *ngIf="cashDetail.unallottedEcf > 0">
                <div class="tw-h-8 tw-w-px tw-shrink-0 tw-bg-divider"></div>
                <div class="tw-flex tw-flex-1 tw-flex-col tw-items-center tw-gap-0.5">
                  <span class="tw-text-xs tw-font-semibold tw-leading-4 tw-text-body"
                    >{{ 'dashboard.ecfInvestment' | translate }}</span
                  >
                  <span class="tw-text-sm tw-font-semibold tw-leading-5 tw-text-black"
                    >RM {{ cashDetail.unallottedEcf | number: '1.0-0' }}</span
                  >
                </div>
              </ng-container>
            </ng-container>
          </div>

          <a
            *ngIf="selectedSegment"
            [routerLink]="selectedSegment.detailsLink"
            class="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-pt-2 tw-text-sm tw-font-semibold tw-text-secondary"
          >
            <span>{{ 'dashboard.viewDetails' | translate }}</span>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="m9 18 6-6-6-6" />
            </svg>
          </a>
        </div>
      </div>
      <!-- /donut card -->

      <!-- FRC-1029 state B: says why the figure is masked, without naming it. Gated on
         !isLoading like the donut itself — otherwise it prints under a still-spinning
         chart, explaining a mask that is not on screen yet. -->
      <p
        *ngIf="!isLoading && hasWithheldShares"
        class="tw-m-0 tw-text-center tw-text-base tw-font-semibold tw-text-body-dark"
      >
        {{ 'dashboard.withheldShares' | translate }}
      </p>

      <!-- Action buttons (icons + routing exact per Figma 472:646 annotations) -->
      <div class="tw-grid tw-w-full tw-grid-cols-4 tw-gap-4">
        <!-- Top Up → PSTX account page, open top-up modal -->
        <a routerLink="/pstx/account" [queryParams]="{ topup: 'true' }" class="action-btn">
          <svg
            class="tw-text-body-dark"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            stroke-width="1.66667"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M10 14.1667V2.5" />
            <path d="M5 9.16667L10 14.1667L15 9.16667" />
            <path d="M15.8333 17.5H4.16667" />
          </svg>
          <span>{{ 'dashboard.quickActions.topUp' | translate }}</span>
        </a>
        <!-- Withdraw → PSTX account page, open withdraw modal -->
        <a routerLink="/pstx/account" [queryParams]="{ withdraw: 'true' }" class="action-btn">
          <svg
            class="tw-text-body-dark"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            stroke-width="1.66667"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M15 7.5L10 2.5L5 7.5" />
            <path d="M10 2.5V14.1667" />
            <path d="M4.16667 17.5H15.8333" />
          </svg>
          <span>{{ 'dashboard.quickActions.withdraw' | translate }}</span>
        </a>
        <!-- Invest → active ECF campaigns page -->
        <a routerLink="/equity" class="action-btn">
          <svg
            class="tw-text-body-dark"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            stroke-width="1.66667"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M13.3333 5.83333H18.3333V10.8333" />
            <path d="M18.3333 5.83333L11.25 12.9167L7.08333 8.75L1.66667 14.1667" />
          </svg>
          <span>{{ 'dashboard.quickActions.invest' | translate }}</span>
        </a>
        <!-- Trade → PSTX trading page -->
        <a routerLink="/pstx" class="action-btn">
          <svg
            class="tw-text-body-dark"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            stroke-width="1.66667"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M13.3333 2.5L16.6667 5.83333L13.3333 9.16667" />
            <path d="M16.6667 5.83333H3.33333" />
            <path d="M6.66667 17.5L3.33333 14.1667L6.66667 10.8333" />
            <path d="M3.33333 14.1667H16.6667" />
          </svg>
          <span>{{ 'dashboard.quickActions.trade' | translate }}</span>
        </a>
      </div>
    </article>

    <!-- ============ THINGS TO DO ============ -->
    <article
      class="tw-flex tw-flex-col tw-gap-4 tw-rounded-2xl tw-border tw-border-divider tw-bg-white tw-p-6 xl:tw-w-[390px] xl:tw-shrink-0"
    >
      <header class="tw-flex tw-items-center tw-justify-between">
        <div class="tw-flex tw-items-center tw-gap-2">
          <h3 class="tw-m-0 tw-text-xl tw-font-black tw-text-body-dark">
            {{ 'dashboard.todo.title' | translate }}
          </h3>
          <button
            type="button"
            #todoTooltip="matTooltip"
            (click)="todoTooltip.toggle()"
            class="btn-transparent tw-inline-flex tw-items-center tw-justify-center tw-text-divider-dark"
            matTooltip="{{ 'dashboard.todo.helper' | translate }}"
            matTooltipPosition="below"
            matTooltipTouchGestures="on"
            aria-label="{{ 'dashboard.todo.helper' | translate }}"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 16v-4" />
              <path d="M12 8h.01" />
            </svg>
          </button>
        </div>
        <span
          class="tw-rounded-full tw-bg-divider-light tw-px-2.5 tw-py-1 tw-text-[13px] tw-font-bold tw-text-body-dark"
        >
          {{ todoCount }} {{ 'dashboard.todo.countSuffix' | translate }}
        </span>
      </header>

      <p
        *ngIf="!(todosLoaded && todoCount === 0)"
        class="tw-m-0 tw-text-sm tw-leading-snug tw-text-body"
      >
        {{ 'dashboard.todo.helper' | translate }}
      </p>

      <!-- All caught up -->
      <div
        *ngIf="todosLoaded && todoCount === 0"
        class="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-py-8 tw-text-center"
      >
        <span
          class="tw-flex tw-h-14 tw-w-14 tw-items-center tw-justify-center tw-rounded-full tw-bg-secondary-lightest tw-text-secondary"
        >
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M21.801 10A10 10 0 1 1 17 3.335" />
            <path d="m9 11 3 3L22 4" />
          </svg>
        </span>
        <span class="tw-text-base tw-font-semibold tw-text-body-dark">{{
          'dashboard.todo.emptyTitle' | translate
        }}</span>
        <span class="tw-max-w-[240px] tw-text-sm tw-leading-snug tw-text-body">
          {{ 'dashboard.todo.emptyDescription' | translate }}
        </span>
      </div>

      <div *ngIf="todoCount > 0" class="tw-flex tw-flex-col tw-gap-3">
        <!-- Verify identity -->
        <a class="todo-card" *ngIf="showEkycTodo" routerLink="/proceed-e-kyc">
          <span class="todo-card__icon tw-bg-warning-light tw-text-warning-dark">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <ng-container *ngIf="hasWithheldShares">
                <circle cx="12" cy="16" r="1" />
                <rect x="3" y="10" width="18" height="12" rx="2" />
                <path d="M7 10V7a5 5 0 0 1 10 0v3" />
              </ng-container>
              <ng-container *ngIf="!hasWithheldShares">
                <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </ng-container>
            </svg>
          </span>
          <span class="todo-card__body">
            <span class="tw-flex tw-items-center tw-gap-2">
              <span class="tw-text-base tw-font-semibold tw-text-body-dark">{{
                (hasWithheldShares ? 'dashboard.todo.claimShares' : 'dashboard.todo.verifyIdentity')
                  | translate
              }}</span>
              <span class="todo-card__count tw-bg-warning-dark">1</span>
            </span>
            <span class="tw-text-sm tw-leading-snug tw-text-body">
              {{
                (hasWithheldShares
                  ? 'dashboard.todo.claimSharesDescription'
                  : 'dashboard.todo.verifyIdentityDescription'
                ) | translate
              }}
            </span>
          </span>
          <svg
            class="todo-card__chevron"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="m9 18 6-6-6-6" />
          </svg>
        </a>

        <!-- Pay for investments -->
        <a
          class="todo-card"
          *ngIf="pendingPaymentCount > 0"
          routerLink="/dashboard/pledges"
          [queryParams]="{ chip: 'Active', payment: 'PendingPayment' }"
        >
          <span class="todo-card__icon tw-bg-ecf-light tw-text-ecf">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect width="20" height="14" x="2" y="5" rx="2" />
              <path d="M2 10h20" />
            </svg>
          </span>
          <span class="todo-card__body">
            <span class="tw-flex tw-items-center tw-gap-2">
              <span class="tw-text-base tw-font-semibold tw-text-body-dark">{{
                'dashboard.todo.payInvestments' | translate
              }}</span>
              <span class="todo-card__count tw-bg-ecf">{{ pendingPaymentCount }}</span>
            </span>
            <span class="tw-text-sm tw-leading-snug tw-text-body">
              {{
                'dashboard.todo.payInvestmentsDescription'
                  | translate: { count: pendingPaymentCount }
              }}
            </span>
          </span>
          <svg
            class="todo-card__chevron"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="m9 18 6-6-6-6" />
          </svg>
        </a>

        <!-- Orders in progress -->
        <a class="todo-card" *ngIf="activeOrdersCount > 0" routerLink="/dashboard/orders">
          <span class="todo-card__icon tw-bg-secondary-lightest tw-text-secondary">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
              <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
              <path d="M12 11h4" />
              <path d="M12 16h4" />
              <path d="M8 11h.01" />
              <path d="M8 16h.01" />
            </svg>
          </span>
          <span class="todo-card__body">
            <span class="tw-flex tw-items-center tw-gap-2">
              <span class="tw-text-base tw-font-semibold tw-text-body-dark">{{
                'dashboard.todo.activeOrders' | translate
              }}</span>
              <span class="todo-card__count tw-bg-secondary">{{ activeOrdersCount }}</span>
            </span>
            <span class="tw-text-sm tw-leading-snug tw-text-body">
              {{ 'dashboard.todo.activeOrdersDescription' | translate }}
            </span>
          </span>
          <svg
            class="todo-card__chevron"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="m9 18 6-6-6-6" />
          </svg>
        </a>

        <!-- Refund in progress -->
        <a
          class="todo-card"
          *ngIf="refundInProgressCount > 0"
          routerLink="/dashboard/pledges"
          [queryParams]="{ chip: 'Active', payment: 'RefundRequested' }"
        >
          <span class="todo-card__icon tw-bg-divider tw-text-body">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M3 7v6h6" />
              <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13" />
            </svg>
          </span>
          <span class="todo-card__body">
            <span class="tw-flex tw-items-center tw-gap-2">
              <span class="tw-text-base tw-font-semibold tw-text-body-dark">{{
                'dashboard.todo.refundInProgress' | translate
              }}</span>
              <span class="todo-card__count tw-bg-body">{{ refundInProgressCount }}</span>
            </span>
            <span class="tw-text-sm tw-leading-snug tw-text-body">
              {{ 'dashboard.todo.refundInProgressDescription' | translate }}
            </span>
          </span>
          <svg
            class="todo-card__chevron"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="m9 18 6-6-6-6" />
          </svg>
        </a>
      </div>
    </article>
  </section>
</main>
